import { inject, injectable } from 'inversify';
import { TYPES } from '@Types';
import {
  army_list,
  army_list_dto,
  army_list_full,
  army_list_last_update,
} from '@Api';
import {
  ArmyList,
  ArmyListSize,
  ArmyListVersioning,
  VersionGame,
} from '@Models';
import { ArmyListUnitMapper } from './army-list-unit.mapper';
import { IMapper } from '../IMapper';
import { VersionGameMapper } from '@Mapper/ninthAge/VersionGameMapper';

@injectable()
export class ArmyListMapper
  implements IMapper<string, army_list_full, army_list, ArmyList>
{
  @inject(TYPES.ArmyListUnitMapper)
  private _armyListUnitMapper!: ArmyListUnitMapper;
  @inject(TYPES.Mapper.VersionGameMapper)
  private _versionGameMapper!: VersionGameMapper;

  public map(item: army_list_full): ArmyList {
    const versionGame = this._versionGameMapper.map(item.version_game);
    return {
      id: item.id,
      initials: item.initials,
      largeLogo: item.large_logo,
      creator: '',
      updatedAt: item.updated_at,
      versionId: item.version_id,
      versionName: item.version_name,
      versionColor: item.version_color ?? '#ff0000',
      versionGame: versionGame,
      armyId: item.army_id,
      name: item.name,
      points: item.points,
      max: item.max,
      size: this.mapArmyListSize(versionGame, item.max),
      folderId: item.folder_id,
      army_organisation_id: item.army_organisation_id,
      army_list_units: [],
      isPrivate: item.private,
      notes: null,
    } satisfies ArmyList as ArmyList;
  }

  public mapFull(item: army_list): ArmyList {
    const versionGame = this._versionGameMapper.map(item.version_game);
    return {
      id: item.id,
      initials: item.initials,
      largeLogo: item.large_logo,
      creator: item.creator,
      updatedAt: item.updated_at,
      versionId: item.version_id,
      versionName: item.version_name,
      versionColor: item.version_color ?? '',
      versionGame: versionGame,
      armyId: item.army_id,
      name: item.name,
      points: item.points,
      max: item.max,
      size: this.mapArmyListSize(versionGame, item.max),
      folderId: item.folder_id,
      army_organisation_id: item.army_organisation_id,
      army_list_units: item.army_list_units.map((unit) =>
        this._armyListUnitMapper.map(unit),
      ),
      isPrivate: item.private,
      notes: item.notes,
    } satisfies ArmyList as ArmyList;
  }

  public mapDto(armyList: ArmyList): army_list_dto {
    return {
      id: armyList.id,
      max: armyList.max,
      army_id: armyList.armyId,
      name: armyList.name,
      army_organisation_id: armyList.army_organisation_id,
      folder_id: armyList.folderId,
      value_points: armyList.points,
      version_id: armyList.versionId,
      notes: armyList.notes,
      army_list_units_attributes: armyList.army_list_units.map((item) =>
        this._armyListUnitMapper.mapDto(item),
      ),
      private: armyList.isPrivate,
    };
  }

  mapVersioning(item: army_list_last_update): ArmyListVersioning {
    return new ArmyListVersioning({
      id: item.id,
      name: item.name,
      updatedAt: item.updated_at,
      armyId: item.army_id,
    });
  }

  mapArmyListSize(game: VersionGame, max: number): ArmyListSize {
    if (game !== VersionGame.NinthAge) return ArmyListSize.Standard;

    if (max <= 3000) return ArmyListSize.Warbands;
    else if (max >= 8000) return ArmyListSize.GrandArmies;

    return ArmyListSize.Standard;
  }
}
